import db from "../db/database";
import { updateSyncStatus } from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";
import { anotarError } from "./syncLog";
import {
  avisoDeAperturaSinFormasDePago,
  estadoDeLasFormasDePago,
  formasDePagoQueSeOfrecen,
} from "./disponibilidadDeFormasDePago";
import { getInfoCajaActiva } from "./cajaEstadoService";
import { usuarioEnSesion } from "./usuarioEnSesion";

/**
 * Qué formas de pago puede cobrar cada tienda.
 *
 * Lee el servicio `FormasPagoTienda` --`page 75242 "Formas Pago Tienda TPV"`,
 * sobre `Payment Method Det TPV`--, que publica exactamente dos propiedades:
 * `FormaPago` y `Tienda`. La cuenta contable se queda del lado de Business
 * Central a propósito.
 *
 * El catálogo general (`formasPago`) **no se toca**: sigue trayendo todas las
 * marcadas `Tpv`, porque hace falta para nombrar la forma de pago de documentos
 * de otras tiendas. Esto es lo que dice cuáles se pueden *elegir*.
 */

/** El nombre con el que esta entidad aparece en `syncMetadata` y en la consola. */
export const ENTIDAD = "formasPagoPorTienda";

/** El servicio web, tal y como se acordó con Business Central el 20/09/2026. */
export const SERVICIO = "FormasPagoTienda";

export const syncFormasDePagoPorTienda = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    const pares = await fetchFromBusinessCentral(SERVICIO);

    // El borrado va **después** de la lectura y sólo si fue bien: si se vaciara
    // antes, un fallo a media lectura dejaría al terminal creyendo que ninguna
    // tienda puede cobrar con nada -- y con la validación de apertura puesta,
    // eso es una caja que no abre por un timeout.
    await db.formasPagoPorTienda.clear();

    const filas = (pares ?? [])
      .map((par) => ({
        Tienda: String(par?.Tienda ?? "").trim(),
        FormaPago: String(par?.Payment_Method ?? par?.FormaPago ?? "").trim(),
        lastSynced: new Date(),
      }))
      .filter((fila) => fila.Tienda && fila.FormaPago);

    if (filas.length) {
      await db.formasPagoPorTienda.bulkAdd(filas);
    }

    // La marca de lectura buena es la que distingue «esta tienda no tiene
    // ninguna» de «todavía no lo hemos podido preguntar». Sin ella, un ambiente
    // donde el servicio aún no está registrado sería indistinguible de una
    // tienda sin configurar, y bloquearíamos cajas que no hay que bloquear.
    await updateSyncStatus(ENTIDAD, true, filas.length);

    return true;
  } catch (error) {
    anotarError("formas de pago por tienda", "falló la lectura", error);
    await updateSyncStatus(ENTIDAD, false, 0, error?.message ?? "");

    return false;
  }
};

/**
 * ¿Se llegó a leer alguna vez esta entidad de Business Central?
 *
 * Mientras la respuesta sea que no --el servicio web todavía no está registrado
 * en ese ambiente, o nunca hubo red-- el terminal **no sabe nada** y no debe
 * decidir nada a partir de este silencio. Es lo que permite desplegar el
 * terminal antes que el servicio sin dejar a nadie sin cobrar.
 */
export const seLeyeronLasFormasDePagoPorTienda = async () => {
  try {
    const marca = await db.syncMetadata.where("entity").equals(ENTIDAD).first();

    return Boolean(marca?.lastSuccessfulSync);
  } catch {
    return false;
  }
};

/**
 * Los códigos de forma de pago que puede cobrar una tienda.
 *
 * @param {string} tienda El código de tienda, tal y como lo trae la caja.
 * @returns {Promise<string[]>} Los códigos, sin repetir.
 */
export const formasDePagoDeLaTienda = async (tienda) => {
  const codigo = String(tienda ?? "").trim();
  if (!codigo) return [];

  const filas = await db.formasPagoPorTienda.where("Tienda").equals(codigo).toArray();

  return [...new Set(filas.map((fila) => fila.FormaPago))];
};

/**
 * La tienda con la que trabaja el terminal ahora mismo.
 *
 * **La de la caja abierta manda**, porque es la que Business Central sella en
 * la cabecera --`SalesHeaderT.Tienda := Caja."Tienda"`-- y por tanto la que
 * `CuentaPago` va a buscar al registrar. Si se usara la del cajero y las dos no
 * coincidieran, el terminal ofrecería formas de pago que el registro rechaza.
 *
 * **Sin caja abierta se usa la del cajero**, que se conoce desde el instante
 * del login: Business Central la manda en el pull de usuarios y viaja en la
 * sesión. Esto es lo que hace que las pantallas de consulta enseñen lo de tu
 * tienda antes de abrir caja, en vez de todo el catálogo del país.
 *
 * Las dos coinciden casi siempre, porque sólo se ofrecen cajas de la tienda del
 * cajero. Se separan justo cuando la del cajero viene vacía, y ahí se ofrecen
 * todas -- otro motivo para que la de la caja tenga preferencia.
 */
export const tiendaEnUso = async () => {
  const caja = await getInfoCajaActiva();
  const deLaCaja = String(caja?.TPV ?? "").trim();
  if (deLaCaja) return deLaCaja;

  return String(usuarioEnSesion()?.Tienda ?? "").trim();
};

/**
 * Las formas de pago con las que puede trabajar el terminal.
 *
 * La usan el selector del cobro y la pantalla de formas de pago. Los otros diez
 * sitios que leen el catálogo lo hacen para *nombrar* una forma de pago
 * --informes, resumen del turno, cierre de caja, históricos-- y ésos tienen que
 * seguir viendo la lista entera: un documento traído de otra tienda enseñaría
 * el código pelado si le recortáramos el catálogo. El filtro vive donde se
 * elige y donde se consulta lo propio, no donde se nombra lo ajeno.
 *
 * @param {object[]} catalogo Lo que devolvió `formaDePagoService.getAll()`.
 */
export const formasDePagoParaCobrar = async (catalogo) => {
  try {
    const tienda = await tiendaEnUso();
    if (!tienda) return catalogo ?? [];

    const [seLeyo, codigos] = await Promise.all([
      seLeyeronLasFormasDePagoPorTienda(),
      formasDePagoDeLaTienda(tienda),
    ]);

    return formasDePagoQueSeOfrecen(catalogo, { seLeyo, codigos });
  } catch (error) {
    // Ante la duda, el catálogo entero: dejar al cajero sin formas de pago por
    // un fallo local es peor que el rechazo que esto viene a evitar.
    anotarError("formas de pago por tienda", "no se pudo filtrar por tienda", error);

    return catalogo ?? [];
  }
};

/**
 * El motivo por el que esta tienda no podría cobrar, o cadena vacía si sí puede
 * --o si no hay forma de saberlo, que no es lo mismo pero se trata igual--.
 *
 * Lo usa la apertura de caja. Devuelve texto y no un booleano porque el aviso
 * tiene que nombrar la tienda: quien lo lea necesita saber cuál configurar.
 *
 * @param {string} tienda El código de tienda de la caja.
 */
export const porQueNoSePuedeCobrarEnLaTienda = async (tienda) => {
  const codigo = String(tienda ?? "").trim();

  // Una caja sin tienda asignada es otro problema, y ya tiene su aviso en el
  // formulario. Aquí no se sabe por qué tienda preguntar, así que no se decide.
  if (!codigo) return "";

  try {
    const [seLeyo, codigos] = await Promise.all([
      seLeyeronLasFormasDePagoPorTienda(),
      formasDePagoDeLaTienda(codigo),
    ]);

    return avisoDeAperturaSinFormasDePago(codigo, estadoDeLasFormasDePago({ seLeyo, codigos }));
  } catch (error) {
    // Un fallo leyendo la base local no es una tienda sin configurar. Que no
    // impida abrir: quedarse sin caja por un tropiezo de Dexie sería peor que
    // el problema que esto viene a evitar.
    anotarError("formas de pago por tienda", "no se pudo comprobar la tienda", error);

    return "";
  }
};
